// Shared types + zod schemas for the HubSpot partner field mapping feature.
// Backend (apps/api) validates request bodies against the schemas below;
// frontend (apps/web) imports the inferred types to keep request shapes in
// sync.
import { z } from "zod";

// HubSpot property primitive types — mirrors `HubSpotPropertyType` in
// hubspot-properties.ts but kept local because the mapping feature works
// with the custom Partner object's full type palette (including booleans),
// not just the constrained form-builder subset.
export const hubspotPartnerPropertyTypeSchema = z.enum([
  "string",
  "enumeration",
  "date",
  "datetime",
  "number",
  "bool",
]);
export type HubspotPartnerPropertyType = z.infer<
  typeof hubspotPartnerPropertyTypeSchema
>;

export const hubspotPartnerPropertyOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});
export type HubspotPartnerPropertyOption = z.infer<
  typeof hubspotPartnerPropertyOptionSchema
>;

// One row from the `hubspot_partner_field_map` table. The HubSpot snapshot
// columns (`hubspot_label`, `hubspot_type`, `hubspot_field_type`,
// `enum_options`) are frozen at insert time and only re-fetched by the
// `refresh-schema` endpoint.
export type HubspotPartnerFieldMapRow = {
  id: string;
  hubspot_property_name: string;
  hubspot_label: string;
  hubspot_type: HubspotPartnerPropertyType;
  hubspot_field_type: string | null;
  enum_options: HubspotPartnerPropertyOption[] | null;
  display_label: string;
  display_section: string;
  sort_order: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  // Derived, not stored: true when the property is no longer in HubSpot.
  is_stale: boolean;
};

// One property returned by the discovery endpoint (filtered + projected
// from HubSpot's properties.coreApi.getAll response).
export type HubspotDiscoverablePartnerProperty = {
  name: string;
  label: string;
  type: HubspotPartnerPropertyType;
  field_type: string | null;
  options: HubspotPartnerPropertyOption[];
  description: string | null;
};

// POST body — add one mapping row. The server re-fetches the HubSpot
// property metadata at insert time and persists the snapshot; the client
// only sends the property name + optional display overrides.
export const hubspotPartnerFieldMapCreateSchema = z.object({
  hubspot_property_name: z.string().min(1).max(200),
  display_label: z.string().min(1).max(200).optional(),
  display_section: z.string().min(1).max(120).optional(),
});
export type HubspotPartnerFieldMapCreateInput = z.infer<
  typeof hubspotPartnerFieldMapCreateSchema
>;

// PATCH body — edit display metadata only. Snapshot columns (type, label,
// enum_options) are immutable except via the refresh-schema endpoint.
export const hubspotPartnerFieldMapUpdateSchema = z.object({
  display_label: z.string().min(1).max(200).optional(),
  display_section: z.string().min(1).max(120).optional(),
  sort_order: z.number().int().min(0).optional(),
  enabled: z.boolean().optional(),
});
export type HubspotPartnerFieldMapUpdateInput = z.infer<
  typeof hubspotPartnerFieldMapUpdateSchema
>;

// Refresh-schema response — surfaces aggregate counts so the toast can show
// "Refreshed N. M no longer in HubSpot." without a second round-trip.
export type HubspotPartnerFieldMapRefreshResult = {
  refreshed: number;
  stale: number;
};
